import { api, session } from './client.js';

/**
 * Typed wrappers over the endpoints the console uses.
 *
 * Grouped by screen so a page imports one object, not a dozen loose functions.
 */

export interface Instance {
  id: string;
  name: string;
  projectId: string;
  phoneNumber: string | null;
  profileName: string | null;
  status: string;
  createdAt: string;
  connectedAt: string | null;
  lastSeenAt: string | null;
}

export interface InstanceDetail extends Instance {
  /** Chave da instância no provedor. Identifica, não autentica. */
  instanceKey: string | null;
}

export interface Message {
  id: string;
  instanceId: string;
  direction: 'INBOUND' | 'OUTBOUND';
  type: string;
  recipient: string | null;
  status: string;
  errorCode: string | null;
  requestId: string | null;
  createdAt: string;
}

export interface ApiKey {
  id: string;
  name: string;
  environment: 'live' | 'test';
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface ApiLog {
  requestId: string;
  method: string;
  path: string;
  capability: string | null;
  statusCode: number;
  errorCode: string | null;
  durationMs: number;
  createdAt: string;
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  status: 'ACTIVE' | 'PAUSED' | 'DISABLED';
  failureCount: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
}

export interface WebhookDelivery {
  id: string;
  endpointId: string;
  eventId: string;
  status: string;
  attempt: number;
  statusCode: number | null;
  errorMessage: string | null;
  latencyMs: number | null;
  nextRetryAt: string | null;
  createdAt: string;
}

export interface UsageMetric {
  metric: string;
  used: number;
  limit: number | null;
  percentUsed: number;
  level: string;
}

export interface Notification {
  id: string;
  type: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO' | 'RESOLVED';
  title: string;
  body: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface TimelineEvent {
  id: string;
  type: string;
  fromStatus: string | null;
  toStatus: string | null;
  message: string | null;
  requestId: string | null;
  occurredAt: string;
}

export interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
}

interface Paged<T> {
  data: T[];
}

export interface LoginResult {
  mfaRequired: boolean;
  challenge?: string;
  token?: string;
  emailVerified?: boolean;
  organizations?: Array<{ id: string; role: string }>;
}

export const auth = {
  login: (email: string, password: string) =>
    api<LoginResult>('/v1/auth/login', { method: 'POST', body: { email, password } }),
  loginMfa: (challenge: string, code: string) =>
    api<LoginResult>('/v1/auth/login/mfa', { method: 'POST', body: { challenge, code } }),
  signup: (input: {
    name: string;
    email: string;
    password: string;
    organizationName: string;
    country: string;
    document?: string;
  }) =>
    api<{ token: string; organization: { id: string; name: string }; emailVerified: boolean }>(
      '/v1/auth/signup',
      { method: 'POST', body: input },
    ),
  verifyEmail: (code: string) =>
    api<{ emailVerified: boolean }>('/v1/auth/email/verify', { method: 'POST', body: { code } }),
  resendEmailCode: () =>
    api<{ retryAfterSeconds: number }>('/v1/auth/email/resend', { method: 'POST' }),
  forgotPassword: (email: string) =>
    api<{ accepted: boolean }>('/v1/auth/password/forgot', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) =>
    api<{ success: boolean }>('/v1/auth/password/reset', {
      method: 'POST',
      body: { token, password },
    }),
  mfaSetup: () => api<{ secret: string; uri: string }>('/v1/auth/mfa/setup', { method: 'POST' }),
  mfaConfirm: (code: string) =>
    api<void>('/v1/auth/mfa/confirm', { method: 'POST', body: { code } }),
  mfaDisable: (password: string, code: string) =>
    api<void>('/v1/auth/mfa/disable', { method: 'POST', body: { password, code } }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<void>('/v1/auth/password/change', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  changeEmail: (password: string, email: string) =>
    api<{ emailVerified: false; retryAfterSeconds: number }>('/v1/auth/email/change', {
      method: 'POST',
      body: { password, email },
    }),
  me: () =>
    api<{
      organizationId: string;
      role: string;
      permissions: string[];
      authMode: string;
      /** Conta da equipe de operação; decide se o console oferece o link para §58. */
      platformAdmin: boolean;
      /** O gate de cadastro: `false` leva à tela de verificação. */
      emailVerified: boolean;
      mfaEnabled: boolean;
      /** Quem está logado; ausente quando a credencial é uma API key. */
      user?: { name: string; email: string };
      /**
       * Presente só quando a credencial em uso é uma concessão de §65.
       *
       * Vem do servidor a cada chamada, e não de um estado guardado aqui: um banner que
       * dependesse do front lembrar de si mesmo sumiria num refresh, e um operador que
       * esquece que está dentro da conta de um cliente é o acidente que §65 evita.
       */
      impersonation?: {
        id: string;
        adminUserId: string;
        reason: string;
        expiresAt: string;
        readOnly: boolean;
      };
    }>('/v1/auth/me'),
  logout: () => api<void>('/v1/auth/logout', { method: 'POST' }),
};

export const projects = {
  list: () => api<Paged<{ id: string; name: string; environment: string }>>('/v1/projects'),
  create: (name: string, environment: string) =>
    api<{ id: string }>('/v1/projects', { method: 'POST', body: { name, environment } }),
};

export const instances = {
  list: () => api<Paged<Instance>>('/v1/instances'),
  get: (id: string) => api<InstanceDetail>(`/v1/instances/${id}`),
  create: (name: string, projectId: string) =>
    api<Instance>('/v1/instances', { method: 'POST', body: { name, projectId } }),
  status: (id: string) => api<Instance>(`/v1/instances/${id}/status`),
  qrCode: (id: string) =>
    api<{ code: string; expiresAt: string | null }>(`/v1/instances/${id}/qrcode`),
  pairingCode: (id: string, phoneNumber: string) =>
    api<{ code: string; expiresAt: string | null }>(`/v1/instances/${id}/pairing-code`, {
      method: 'POST',
      body: { phoneNumber },
    }),
  checkNumber: (id: string, phone: string) =>
    api<{ phone: string; exists: boolean; jid: string | null }>(
      `/v1/instances/${id}/check-number`,
      { query: { phone } },
    ),
  connect: (id: string) => api<Instance>(`/v1/instances/${id}/connect`, { method: 'POST' }),
  restart: (id: string) => api<Instance>(`/v1/instances/${id}/restart`, { method: 'POST' }),
  disconnect: (id: string) => api<Instance>(`/v1/instances/${id}/disconnect`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/v1/instances/${id}`, { method: 'DELETE' }),
  timeline: (id: string) => api<Paged<TimelineEvent>>(`/v1/instances/${id}/timeline`),
  diagnose: (id: string) =>
    api<{
      checks: Array<{ component: string; ok: boolean; detail?: string }>;
      conclusion: string;
      recommendation: string | null;
    }>(`/v1/instances/${id}/diagnose`),
};

export const messages = {
  list: (filter: Record<string, string | undefined> = {}) =>
    api<Paged<Message>>('/v1/messages', { query: filter }),
  sendText: (instanceId: string, to: string, text: string) =>
    api<Message>('/v1/messages/text', { method: 'POST', body: { instanceId, to, text } }),
};

export const apiKeys = {
  list: () => api<Paged<ApiKey>>('/v1/api-keys'),
  create: (name: string, environment: 'live' | 'test') =>
    api<ApiKey & { secret: string }>('/v1/api-keys', {
      method: 'POST',
      body: { name, environment },
    }),
  revoke: (id: string) => api<void>(`/v1/api-keys/${id}`, { method: 'DELETE' }),
};

export const logs = {
  list: (filter: { requestId?: string; statusCode?: string } = {}) =>
    api<Paged<ApiLog>>('/v1/logs/requests', { query: filter }),
};

export interface AuditEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  reason: string | null;
  requestId: string | null;
  occurredAt: string;
  actor: { type: string; label: string; apiKeyPrefix?: string };
  details: Record<string, unknown> | null;
}

export const audit = {
  list: (filter: { action?: string; actorType?: string } = {}) =>
    api<Paged<AuditEntry>>('/v1/audit', { query: filter }),
};

export const webhooks = {
  list: () => api<Paged<WebhookEndpoint>>('/v1/webhooks'),
  create: (url: string, events: string[]) =>
    api<WebhookEndpoint & { secret: string }>('/v1/webhooks', {
      method: 'POST',
      body: { url, events },
    }),
  update: (id: string, patch: Record<string, unknown>) =>
    api<WebhookEndpoint>(`/v1/webhooks/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => api<void>(`/v1/webhooks/${id}`, { method: 'DELETE' }),
  sendTest: (id: string, type: string) =>
    api<{ deliveryId: string }>(`/v1/webhooks/${id}/test`, { method: 'POST', body: { type } }),
  deliveries: (filter: Record<string, string | undefined> = {}) =>
    api<Paged<WebhookDelivery>>('/v1/webhooks/deliveries', { query: filter }),
  dlq: () => api<Paged<WebhookDelivery>>('/v1/webhooks/dlq'),
  replay: (deliveryId: string) =>
    api<{ id: string }>(`/v1/webhooks/deliveries/${deliveryId}/replay`, { method: 'POST' }),
};

export const usage = {
  current: () =>
    api<{ plan: { code: string; name: string }; period: string; metrics: UsageMetric[] }>(
      '/v1/usage',
    ),
  series: (metric: string) =>
    api<{ metric: string; data: Array<{ date: string; quantity: number }> }>('/v1/usage/series', {
      query: { metric },
    }),
};

export const notifications = {
  list: () => api<Paged<Notification>>('/v1/notifications'),
  markRead: (id: string) => api<void>(`/v1/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => api<{ updated: number }>('/v1/notifications/read-all', { method: 'POST' }),
};

export const team = {
  list: () =>
    api<
      Paged<{
        id: string;
        userId: string;
        name: string | null;
        email: string | null;
        role: string;
        status: string;
      }>
    >('/v1/members'),
  changeRole: (membershipId: string, role: string) =>
    api<{ role: string }>(`/v1/members/${membershipId}`, { method: 'PATCH', body: { role } }),
};

export interface Plan {
  code: string;
  name: string;
  tier: 'trial' | 'start' | 'business' | 'enterprise' | string;
  instanceCount: number;
  priceCents: number;
  currency: string;
  limits: Record<string, number>;
}

export type PaymentMethod = 'CARD' | 'BOLETO' | 'PIX';

export interface Payment {
  id: string;
  invoiceId: string | null;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED' | string;
  method: PaymentMethod | string;
  amountCents: number;
  currency: string;
  checkoutUrl: string | null;
  receiptUrl: string | null;
  failureReason: string | null;
  planCode: string | null;
  dueAt: string | null;
  createdAt: string;
  settledAt: string | null;
}

export interface Subscription {
  status: string;
  plan: Plan | null;
  trialEnd: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  canceledAt: string | null;
  capabilities: { canRead: boolean; canWrite: boolean; canSendMessages: boolean };
  pendingPayment: Payment | null;
  checkoutAvailable: boolean;
  paymentMethods: PaymentMethod[];
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  totalCents: number;
  periodStart: string;
  periodEnd: string;
  dueAt: string | null;
  paidAt: string | null;
}

export const billing = {
  plans: () => api<Paged<Plan>>('/v1/plans'),
  subscription: () => api<Subscription | null>('/v1/billing/subscription'),
  changePlan: (planCode: string) =>
    api<{ status: string }>('/v1/billing/subscription', { method: 'POST', body: { planCode } }),
  checkout: (planCode: string, method: PaymentMethod) =>
    api<Payment & { reused: boolean }>('/v1/billing/checkout', {
      method: 'POST',
      body: { planCode, method, returnUrl: `${window.location.origin}/billing` },
    }),
  payments: (range?: { from?: string; to?: string }) =>
    api<Paged<Payment>>('/v1/billing/payments', {
      query: {
        ...(range?.from ? { from: range.from } : {}),
        ...(range?.to ? { to: range.to } : {}),
      },
    }),
  payment: (id: string) => api<Payment>(`/v1/billing/payments/${id}`),
  cancelInvoice: (invoiceId: string) =>
    api<{ id: string; status: 'VOID' }>(`/v1/billing/invoices/${invoiceId}/cancel`, {
      method: 'POST',
    }),
  invoices: () => api<Paged<Invoice>>('/v1/billing/invoices'),
};

export interface ExplorerParameter {
  name: string;
  in: 'path' | 'query' | 'header';
  required: boolean;
  type: string;
  description: string;
  example?: string | number | boolean;
  enum?: string[];
}

export interface ExplorerField {
  name: string;
  type: string;
  required?: boolean;
  description: string;
  example?: unknown;
  enum?: string[];
}

export interface ExplorerOperation {
  operationId: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  summary: string;
  tag: string;
  capability: string;
  permission: string | null;
  idempotent: boolean;
  parameters: ExplorerParameter[];
  body: ExplorerField[];
  allowed: boolean;
}

export interface ExecutionResult {
  status: number;
  ok: boolean;
  durationMs: number;
  requestId: string | null;
  body: unknown;
}

export interface McpTool {
  name: string;
  description: string;
  inputSchema: { type: 'object'; properties: Record<string, unknown>; required?: string[] };
}

export const developers = {
  onboarding: () =>
    api<{ steps: OnboardingStep[]; percent: number; nextStep: string | null }>('/v1/onboarding'),
  operations: () => api<Paged<ExplorerOperation>>('/v1/explorer/operations'),
  samples: (
    operationId: string,
    values?: {
      path?: Record<string, string>;
      query?: Record<string, string>;
      body?: Record<string, unknown>;
    },
  ) =>
    api<{ samples: Record<string, string>; languages: string[] }>(
      `/v1/explorer/operations/${operationId}/samples`,
      values ? { method: 'POST', body: values } : {},
    ),
  mcpTools: () => api<Paged<McpTool>>('/v1/mcp/tools'),
  /**
   * Executa uma operação do contrato com a sessão do console (PRD §32).
   *
   * Não passa pelo `api()`: aqui o status, a latência e o request id de uma resposta
   * de erro são o resultado, não uma exceção.
   */
  execute: async (input: {
    method: string;
    path: string;
    query?: Record<string, string>;
    body?: unknown;
  }): Promise<ExecutionResult> => {
    const url = new URL(input.path, window.location.origin);
    for (const [key, value] of Object.entries(input.query ?? {})) {
      if (value !== '') url.searchParams.set(key, value);
    }
    const headers: Record<string, string> = {};
    if (session.token) headers.authorization = `Bearer ${session.token}`;
    if (session.organizationId) headers['x-talkium-organization'] = session.organizationId;
    if (input.body !== undefined) headers['content-type'] = 'application/json';
    if (input.method !== 'GET') headers['idempotency-key'] = crypto.randomUUID();

    const startedAt = performance.now();
    const response = await fetch(url.toString(), {
      method: input.method,
      headers,
      ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
    });
    const text = await response.text();
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }
    return {
      status: response.status,
      ok: response.ok,
      durationMs: Math.round(performance.now() - startedAt),
      requestId: response.headers.get('x-request-id'),
      body,
    };
  },
};

export const support = {
  tickets: () =>
    api<
      Paged<{ id: string; subject: string; status: string; priority: string; createdAt: string }>
    >('/v1/support/tickets'),
  open: (input: { category: string; subject: string; description: string; instanceId?: string }) =>
    api<{ id: string }>('/v1/support/tickets', { method: 'POST', body: input }),
};
